import {
  GetDeliveryRes,
  GetDeliveryRes_StopLineItem as StopLineItemPB,
  GetDeliveryRes_StopLineItem_AttachedTransactionData as AttachedTransactionPB,
} from "gen/api/delivery/service_pb"
import { Timestamp as TimestampPB } from "gen/google/protobuf/timestamp_pb"

import {
  adaptDeliveryAttempts,
  adaptDeliveryStopDetails,
} from "./delivery.adapter"
import {
  adaptDeliveryStatus,
  adaptDeliveryStopStatus,
  adaptSaleStatus,
  BaseDelivery,
  BaseDeliveryStop,
  StopTransaction,
} from "./delivery.type"
import { FieldMapping, mapFields, mapFieldsArray } from "../adapters"
import { Money } from "../classes/money"
import { UUID } from "../classes/uuid"
import { ensure } from "../utils"

const deliveryDetailsMapping: FieldMapping<GetDeliveryRes, BaseDelivery> = {
  id: (res) => UUID.fromPBOrThrow(res.id, "Missing delivery.id"),

  identifier: (res) => ensure(res.identifier, "Missing delivery.identifier"),

  status: (res) => adaptDeliveryStatus(res.status),

  departureTime: (res) =>
    TimestampPB.toDate(
      ensure(res.departureTime, "Missing delivery.departureTime")
    ),

  isDepartureTimeEntered: (res) => res.isDepartureTimeEntered,

  // TODO (Umer): Update GetDelivery endpoint to return returnTime
  returnTime: () => undefined,

  stops: (res) =>
    mapFieldsArray(res.stopLineItems, deliveryStopMapping).sort(
      (a, b) => a.position - b.position
    ),

  // TODO (Umer): Update GetDelivery endpoint to return dispatchLocation
  dispatchLocation: () => undefined,

  carrier: (res) => res.carrier,

  driver: (res) => {
    const assignedTo = res.assignedTo
    if (!assignedTo) return undefined
    return {
      id: assignedTo.id && UUID.fromPB(assignedTo.id),
      name: `${assignedTo.firstName} ${assignedTo.lastName}`,
    }
  },

  notes: (res) => res.notes,

  internalNotes: (res) => res.internalNotes,
}

const deliveryStopMapping: FieldMapping<StopLineItemPB, BaseDeliveryStop> = {
  id: (item) => UUID.fromPBOrThrow(item.stop?.id, "Missing stop.id"),

  status: (item) => adaptDeliveryStopStatus(item.stop?.status),

  position: (item) => ensure(item.stop?.position, "Missing stop.position"),

  details: (item) => adaptDeliveryStopDetails(item.stop?.details),

  transactions: (item) =>
    mapFieldsArray(item.attachedTransactionData, stopTransactionMapping),

  internalNotes: (item) => item.stop?.internalNotes ?? "",

  deliveryAttempts: (item) =>
    adaptDeliveryAttempts(item.stop?.deliveryAttemptsById),
}

const stopTransactionMapping: FieldMapping<
  AttachedTransactionPB,
  StopTransaction
> = {
  id: (txn) => UUID.fromPBOrThrow(txn.sale?.id, "Missing sale.id"),

  identifier: (txn) => ensure(txn.sale?.identifier, "Missing sale.identifier"),

  numItems: (txn) => txn.saleProductData.length,

  saleStatus: (txn) => adaptSaleStatus(txn.sale?.status),

  total: (txn) => Money.fromPB(txn.sale?.total),

  customerName: (txn) => txn.customer?.name ?? "",

  customerId: (txn) =>
    txn.customer?.id ? UUID.fromPB(txn.customer.id) : undefined,
}

export function adaptDeliveryDetails(res: GetDeliveryRes): BaseDelivery {
  return mapFields(res, deliveryDetailsMapping).data
}
